import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AccountCollectionPanel } from '../../components/accounts/AccountCollectionPanel';
import { CollectionMonthDocumentsModal } from '../../components/accounts/CollectionMonthDocumentsModal';
import { MoneyAmount } from '../../components/accounts/MoneyAmount';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  buildCollectionAgingLabels,
  docMatchesOverviewAgingBucket,
  fetchAccountCollectionSummary,
  fetchAccountsCollectionOverview,
  overviewAgingMonthTitle,
  paymentStatusFromOverviewRow,
  type AccountCollectionDocument,
  type AccountCollectionMonth,
  type AccountsCollectionOverview,
  type AccountsCollectionOverviewRow,
  type CollectionPaymentStatusInfo,
  type OverviewAgingBucket,
} from '../../lib/accountCollection';
import { getDbSupabase } from '../../lib/supabase';
import { usePortal } from '../../contexts/PortalContext';
import { compareSalespersonCodes, formatSalespersonCode, normalizeSalespersonCode } from '../../utils/salespersonScope';

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}

type OverviewSortKey =
  | 'mother_code'
  | 'mother_name'
  | 'salesperson_code'
  | 'payment'
  | 'outstanding_lcy'
  | 'm0_lcy'
  | 'm1_lcy'
  | 'm2_lcy'
  | 'm3_lcy'
  | 'before_lcy'
  | 'overdue_lcy'
  | 'overdue_pct';

type PaymentFilter = 'all' | 'good' | 'slow' | 'bad_debt';

const PAYMENT_RANK: Record<CollectionPaymentStatusInfo['status'], number> = {
  bad_debt: 0,
  slow: 1,
  good: 2,
};

type DocsDrillState = {
  motherCode: string;
  bucket: OverviewAgingBucket;
  title: string;
  documents: AccountCollectionDocument[];
  months: AccountCollectionMonth[];
};

export function AccountsCollectionPage() {
  const { motherCode } = useParams<{ motherCode?: string }>();
  const navigate = useNavigate();
  const { basePath } = usePortal();
  const [data, setData] = useState<AccountsCollectionOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [salespersonFilter, setSalespersonFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilter>('all');
  const [sortKey, setSortKey] = useState<OverviewSortKey>('overdue_lcy');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [crmCompanyNameByCode, setCrmCompanyNameByCode] = useState<Record<string, string>>({});
  const [docsDrill, setDocsDrill] = useState<DocsDrillState | null>(null);
  const [docsLoadingKey, setDocsLoadingKey] = useState<string | null>(null);
  const [docsError, setDocsError] = useState<string | null>(null);
  const docsLoadingLock = useRef(false);
  const [detailCache, setDetailCache] = useState<
    Record<string, { documents: AccountCollectionDocument[]; months: AccountCollectionMonth[] }>
  >({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchAccountsCollectionOverview()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load collection overview.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!data?.accounts?.length) return;
    let cancelled = false;
    const codes = data.accounts.map((a) => a.mother_code);
    void (async () => {
      try {
        const { data: rows } = await getDbSupabase('crm')
          .from('clients')
          .select('code, company_name')
          .in('code', codes);
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const row of rows ?? []) {
          const code = String((row as { code?: string }).code ?? '').trim().toUpperCase();
          const name = String((row as { company_name?: string }).company_name ?? '').trim();
          if (code && name) map[code] = name;
        }
        setCrmCompanyNameByCode(map);
      } catch {
        // keep mother_name from overview
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data]);

  const paymentByCode = useMemo(() => {
    const map: Record<string, CollectionPaymentStatusInfo> = {};
    for (const row of data?.accounts ?? []) {
      map[row.mother_code] = paymentStatusFromOverviewRow(row);
    }
    return map;
  }, [data]);

  const salespersonOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of data?.accounts ?? []) {
      const code = formatSalespersonCode(row.salesperson_code ?? '');
      if (code) set.add(code);
    }
    return [...set].sort(compareSalespersonCodes);
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const spFilterNorm = normalizeSalespersonCode(salespersonFilter);
    return (data?.accounts ?? []).filter((row) => {
      if (spFilterNorm && normalizeSalespersonCode(row.salesperson_code ?? '') !== spFilterNorm) {
        return false;
      }
      const payment = paymentByCode[row.mother_code];
      if (paymentFilter !== 'all' && payment?.status !== paymentFilter) return false;
      if (!q) return true;
      const name = (crmCompanyNameByCode[row.mother_code] ?? row.mother_name).toLowerCase();
      return row.mother_code.toLowerCase().includes(q) || name.includes(q);
    });
  }, [
    data?.accounts,
    search,
    salespersonFilter,
    paymentFilter,
    paymentByCode,
    crmCompanyNameByCode,
  ]);

  const sorted = useMemo(() => {
    const mul = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'mother_code':
        case 'mother_name':
        case 'salesperson_code':
          cmp = String(a[sortKey] ?? '').localeCompare(String(b[sortKey] ?? ''));
          break;
        case 'payment':
          cmp =
            PAYMENT_RANK[paymentByCode[a.mother_code]!.status] -
            PAYMENT_RANK[paymentByCode[b.mother_code]!.status];
          break;
        case 'outstanding_lcy':
        case 'm0_lcy':
        case 'm1_lcy':
        case 'm2_lcy':
        case 'm3_lcy':
        case 'before_lcy':
        case 'overdue_lcy':
        case 'overdue_pct':
          cmp = a[sortKey] - b[sortKey];
          break;
        default:
          cmp = 0;
      }
      if (cmp !== 0) return cmp * mul;
      return a.mother_code.localeCompare(b.mother_code);
    });
  }, [filtered, sortKey, sortDir, paymentByCode]);

  function handleSort(key: OverviewSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(
      key === 'mother_code' ||
        key === 'mother_name' ||
        key === 'salesperson_code' ||
        key === 'payment'
        ? 'asc'
        : 'desc',
    );
  }

  const openAgingDocs = useCallback(
    async (row: AccountsCollectionOverviewRow, bucket: OverviewAgingBucket) => {
      if (docsLoadingLock.current) return;
      const amount =
        bucket === 'm0'
          ? row.m0_lcy
          : bucket === 'm1'
            ? row.m1_lcy
            : bucket === 'm2'
              ? row.m2_lcy
              : bucket === 'm3'
                ? row.m3_lcy
                : row.before_lcy;
      if (Math.abs(amount) < 0.005) return;

      const loadKey = `${row.mother_code}:${bucket}`;
      docsLoadingLock.current = true;
      setDocsError(null);
      setDocsLoadingKey(loadKey);
      try {
        let detail = detailCache[row.mother_code];
        if (!detail) {
          const summary = await fetchAccountCollectionSummary(row.mother_code);
          detail = { documents: summary.documents, months: summary.months };
          setDetailCache((prev) => ({ ...prev, [row.mother_code]: detail! }));
        }
        const today = data?.meta.today ?? null;
        setDocsDrill({
          motherCode: row.mother_code,
          bucket,
          title: `${overviewAgingMonthTitle(bucket, today, detail.months)} · documents`,
          documents: detail.documents,
          months: detail.months,
        });
      } catch (err) {
        setDocsError(err instanceof Error ? err.message : 'Failed to load documents.');
      } finally {
        docsLoadingLock.current = false;
        setDocsLoadingKey(null);
      }
    },
    [data?.meta.today, detailCache],
  );

  if (motherCode) {
    const code = decodeURIComponent(motherCode).toUpperCase();
    const baseName = data?.accounts.find((row) => row.mother_code === code)?.mother_name ?? code;
    const name = crmCompanyNameByCode[code] ?? baseName;
    return (
      <div className="accounts-collection-page">
        <PageHeader title={name} description={`Collection detail · ${code}`} />
        <p className="accounts-collection-page__back">
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => navigate(`${basePath}/collection`)}
          >
            ← All customers
          </button>
        </p>
        <AccountCollectionPanel motherCode={code} companyName={name} />
      </div>
    );
  }

  const summary = data?.summary;
  const agingLabels =
    data?.meta.aging_labels ?? buildCollectionAgingLabels(data?.meta.today ?? null);
  const today = data?.meta.today ?? null;

  return (
    <div className="accounts-collection-page">
      <PageHeader
        title="Collection"
        description="Division 10 aged AR overview. Select a month amount to view invoice documents."
      />

      {summary ? (
        <div className="accounts-collection-kpis">
          <div className="accounts-collection-kpi">
            <span className="accounts-collection-kpi__label">Accounts</span>
            <span className="accounts-collection-kpi__value">
              {summary.account_count.toLocaleString()}
            </span>
          </div>
          <div className="accounts-collection-kpi">
            <span className="accounts-collection-kpi__label">Outstanding</span>
            <span className="accounts-collection-kpi__value">
              <MoneyAmount value={summary.outstanding_lcy} />
            </span>
          </div>
          <div className="accounts-collection-kpi accounts-collection-kpi--warn">
            <span className="accounts-collection-kpi__label">Overdue</span>
            <span className="accounts-collection-kpi__value">
              <MoneyAmount value={summary.overdue_lcy} overdue={summary.overdue_lcy > 0.005} />
            </span>
          </div>
          <div className="accounts-collection-kpi">
            <span className="accounts-collection-kpi__label">Overdue %</span>
            <span className="accounts-collection-kpi__value">
              {formatPct(summary.overdue_pct)}
            </span>
          </div>
        </div>
      ) : null}

      <div className="accounts-collection-toolbar">
        <input
          className="field-input"
          type="search"
          placeholder="Search by mother code or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search accounts"
        />
        <select
          className="field-input accounts-collection-toolbar__select"
          value={salespersonFilter}
          onChange={(e) => setSalespersonFilter(e.target.value)}
          aria-label="Filter by salesperson"
        >
          <option value="">All salespersons</option>
          {salespersonOptions.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
        <select
          className="field-input accounts-collection-toolbar__select"
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value as PaymentFilter)}
          aria-label="Filter by payment status"
        >
          <option value="all">All payment status</option>
          <option value="good">Good Payment</option>
          <option value="slow">Slow Payment</option>
          <option value="bad_debt">Potential Bad Debt</option>
        </select>
        <p className="accounts-collection-toolbar__meta">
          {docsLoadingKey
            ? 'Opening documents…'
            : loading
              ? 'Loading…'
              : `${sorted.length.toLocaleString()} account${sorted.length === 1 ? '' : 's'}`}
        </p>
      </div>

      {error ? <p className="inventory-page__error">{error}</p> : null}
      {docsError ? <p className="inventory-page__error">{docsError}</p> : null}

      <div className="accounts-collection-table-wrap">
        <table className="accounts-collection-table">
          <thead>
            <tr>
              <OverviewSortTh
                label="Mother"
                sortKey="mother_code"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
              <OverviewSortTh
                label="Name"
                sortKey="mother_name"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
              <OverviewSortTh
                label="Salesperson"
                sortKey="salesperson_code"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
              <OverviewSortTh
                label="Payment"
                sortKey="payment"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
              <OverviewSortTh
                label="Overdue %"
                sortKey="overdue_pct"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                numeric
              />
              <OverviewSortTh
                label="Outstanding"
                sortKey="outstanding_lcy"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                numeric
              />
              <OverviewSortTh
                label={agingLabels.m0}
                sortKey="m0_lcy"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                numeric
                aging
              />
              <OverviewSortTh
                label={agingLabels.m1}
                sortKey="m1_lcy"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                numeric
                aging
              />
              <OverviewSortTh
                label={agingLabels.m2}
                sortKey="m2_lcy"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                numeric
                aging
              />
              <OverviewSortTh
                label={agingLabels.m3}
                sortKey="m3_lcy"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                numeric
                aging
              />
              <OverviewSortTh
                label={agingLabels.before}
                sortKey="before_lcy"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
                numeric
                aging
              />
            </tr>
          </thead>
          <tbody>
            {!loading && sorted.length === 0 ? (
              <tr>
                <td colSpan={11} className="accounts-collection-table__empty">
                  No open AR accounts.
                </td>
              </tr>
            ) : (
              sorted.map((row) => (
                <OverviewRow
                  key={row.mother_code}
                  row={row}
                  companyName={crmCompanyNameByCode[row.mother_code] ?? row.mother_name}
                  payment={paymentByCode[row.mother_code]!}
                  to={`${basePath}/collection/${encodeURIComponent(row.mother_code)}`}
                  docsLoadingKey={docsLoadingKey}
                  onOpenAging={(bucket) => void openAgingDocs(row, bucket)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {docsDrill ? (
        <CollectionMonthDocumentsModal
          title={docsDrill.title}
          documents={docsDrill.documents}
          matchDoc={(doc) =>
            docMatchesOverviewAgingBucket(doc, docsDrill.bucket, today)
          }
          onClose={() => setDocsDrill(null)}
        />
      ) : null}
    </div>
  );
}

function OverviewSortTh({
  label,
  sortKey,
  activeKey,
  dir,
  numeric,
  aging,
  onSort,
}: {
  label: string;
  sortKey: OverviewSortKey;
  activeKey: OverviewSortKey;
  dir: 'asc' | 'desc';
  numeric?: boolean;
  aging?: boolean;
  onSort: (key: OverviewSortKey) => void;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      className={[
        'accounts-collection-table__th--sort',
        numeric ? 'num' : '',
        aging ? 'accounts-collection-table__th--aging' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="accounts-collection-table__sort-btn"
        onClick={() => onSort(sortKey)}
      >
        <span>{label}</span>
        <span className="accounts-collection-table__sort-ind" aria-hidden>
          {active ? (dir === 'asc' ? '▲' : '▼') : ''}
        </span>
      </button>
    </th>
  );
}

function AgingAmountButton({
  amount,
  overdueLcy,
  loading,
  disabled,
  onClick,
}: {
  amount: number;
  overdueLcy?: number;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const hasAmount = Math.abs(amount) > 0.005;
  const overdue = (overdueLcy ?? 0) > 0.005;
  if (!hasAmount) {
    return (
      <span className="num accounts-collection-table__aging">
        <MoneyAmount value={0} />
      </span>
    );
  }
  return (
    <button
      type="button"
      className={[
        'accounts-collection-table__aging-btn',
        overdue ? 'accounts-collection-table__aging-btn--overdue' : '',
        loading ? 'is-loading' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      title={loading ? 'Opening documents…' : 'View documents for this month'}
    >
      {loading ? (
        <span className="accounts-collection-table__aging-loading">
          <span className="accounts-collection-table__aging-spinner" aria-hidden />
          Opening…
        </span>
      ) : (
        <MoneyAmount value={amount} overdue={overdue} />
      )}
    </button>
  );
}

function OverviewRow({
  row,
  to,
  companyName,
  payment,
  docsLoadingKey,
  onOpenAging,
}: {
  row: AccountsCollectionOverviewRow;
  to: string;
  companyName: string;
  payment: CollectionPaymentStatusInfo;
  docsLoadingKey: string | null;
  onOpenAging: (bucket: OverviewAgingBucket) => void;
}) {
  const tone =
    payment.status === 'bad_debt' ? 'bad' : payment.status === 'slow' ? 'slow' : 'good';
  const badgeStyle: Record<'good' | 'slow' | 'bad', { background: string; color: string }> = {
    good: { background: '#dcfce7', color: '#166534' },
    slow: { background: '#ffedd5', color: '#9a3412' },
    bad: { background: '#fee2e2', color: '#b91c1c' },
  };
  const rowBusy = !!docsLoadingKey;
  const rowLoading = docsLoadingKey?.startsWith(`${row.mother_code}:`) ?? false;
  return (
    <tr className={rowLoading ? 'accounts-collection-table__row--loading' : undefined}>
      <td className="mono">
        <Link to={to} className="accounts-collection-table__link">
          {row.mother_code}
        </Link>
      </td>
      <td>
        <Link to={to} className="accounts-collection-table__link">
          {companyName}
        </Link>
      </td>
      <td className="mono">{formatSalespersonCode(row.salesperson_code ?? '') || '—'}</td>
      <td>
        <span
          style={{ ...badgeStyle[tone], padding: '2px 8px', borderRadius: 999, fontWeight: 700 }}
        >
          {payment.label}
        </span>
      </td>
      <td className="num">{formatPct(row.overdue_pct)}</td>
      <td className="num">
        <MoneyAmount value={row.outstanding_lcy} />
      </td>
      <td className="num">
        <AgingAmountButton
          amount={row.m0_lcy}
          overdueLcy={row.m0_overdue_lcy}
          loading={docsLoadingKey === `${row.mother_code}:m0`}
          disabled={rowBusy}
          onClick={() => onOpenAging('m0')}
        />
      </td>
      <td className="num">
        <AgingAmountButton
          amount={row.m1_lcy}
          overdueLcy={row.m1_overdue_lcy}
          loading={docsLoadingKey === `${row.mother_code}:m1`}
          disabled={rowBusy}
          onClick={() => onOpenAging('m1')}
        />
      </td>
      <td className="num">
        <AgingAmountButton
          amount={row.m2_lcy}
          overdueLcy={row.m2_overdue_lcy}
          loading={docsLoadingKey === `${row.mother_code}:m2`}
          disabled={rowBusy}
          onClick={() => onOpenAging('m2')}
        />
      </td>
      <td className="num">
        <AgingAmountButton
          amount={row.m3_lcy}
          overdueLcy={row.m3_overdue_lcy}
          loading={docsLoadingKey === `${row.mother_code}:m3`}
          disabled={rowBusy}
          onClick={() => onOpenAging('m3')}
        />
      </td>
      <td className="num">
        <AgingAmountButton
          amount={row.before_lcy}
          overdueLcy={row.before_overdue_lcy}
          loading={docsLoadingKey === `${row.mother_code}:before`}
          disabled={rowBusy}
          onClick={() => onOpenAging('before')}
        />
      </td>
    </tr>
  );
}
